import React from 'react';
import { mapStateToProps, RecommendedActivitiesCarouselProps } from 'component/common/RecommendedActivitiesCarousel/RecommendedActivitiesCarousel.selectors';
import { Box, HorizontalScroll, Typography } from '@drone-js/core';
import { connect } from 'react-redux';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { ActivityGqlOperation, SearchRequestType } from 'store/api/api.types';
import { activityCardMapper } from 'component/common/ActivityCard/ActivityCard.selectors';
import ActivityCard from 'component/common/ActivityCard/ActivityCard';
import { ActivitySingleCardPlaceHolder } from 'component/common/ActivityCard/PlaceHolder/ActivityCardPlaceHolder';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { StringFormatter } from '@agoda/react-handyman';
import { InView } from 'react-intersection-observer';
import { SearchType } from 'gql/autogen/SearchType';

const RecommendedActivitiesCarouselPlaceholder: React.FC<RecommendedActivitiesCarouselProps> = (props) => {
    return (
        <>
            <Typography variant="heading-l" px="4">
                {StringFormatter.formatToJsx(props.title, props.cityName)}
            </Typography>
            <HorizontalScroll pt="16" data-testid={`${props.dataElementName}-placeholder`}>
                {[...Array<number>(props.numberOfActivities)].map((_, i) => (
                    <Box key={i} me="12" width="270px">
                        <ActivitySingleCardPlaceHolder responsive={false} />
                    </Box>
                ))}
            </HorizontalScroll>
        </>
    );
};
RecommendedActivitiesCarouselPlaceholder.displayName = 'RecommendedActivitiesPlaceholder';

const RecommendedActivitiesCarouselComponent: React.FC<RecommendedActivitiesCarouselProps> = (props) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [activitiesList, setActivitiesList] = React.useState<ActivityCardProps[]>([]);
    React.useEffect(() => {
        // The 'searchResultNumber' will change only when resizing the browser. DO not reload the page when user is resizing the browser for better UX.
        setIsLoading(true);
        queryActivitiesGqlApi({
            args: {
                props: {
                    searchType: SearchType.CITY,
                    searchRequestType: props.isPopularActivities ? SearchRequestType.POPULAR_ACTIVITIES : SearchRequestType.DEFAULT,
                    pagination: { number: 1, size: props.numberOfActivities },
                    cityId: props.cityId,
                    filterActivityId: props.filterActivityId,
                    filterType: props.filterType,
                    filterCategory: props.filterCategory,
                    defaultSort: props.defaultSort,
                },
                customStateHandling: true,
            },
            operation: ActivityGqlOperation.search,
            key: props.operationKey,
            onSuccess: (_operation, _args, response) => {
                setIsLoading(false);
                const list = activityCardMapper(response?.result?.activities || [], props.config);
                setActivitiesList(list);
                if (props.setPopularActivitiesResponse) {
                    response && props.setPopularActivitiesResponse(list);
                }
            },
            onError: () => {
                setIsLoading(false);
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.config.pageConfig.cid, props.filterActivityId]);

    const onSeen = (inView: boolean) => {
        if (inView && !isLoading && activitiesList.length > 0) {
            if (!isLoading && activitiesList.length > 0) {
                trackElementEvent<ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesFieldsV2>(ActivitiesTopActivitiesAroundYouCarouselSeenEventActivitiesManifestV2.ids, {
                    action_element_value: props.dataElementName,
                });
            }
        }
    };

    if (isLoading) return <RecommendedActivitiesCarouselPlaceholder {...props} />;
    if (!isLoading && activitiesList.length === 0) return null;

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={1}>
            <Typography variant="heading-l" px="4" pb={props.isPageContainerSpacing ? '12' : undefined} data-testid="detail-page-exploring-carousel-title">
                {StringFormatter.formatToJsx(props.title, props.cityName)}
            </Typography>
            <HorizontalScroll
                active={0}
                nextLabel={props.config.cms[130706]}
                previousLabel={props.config.cms[130765]}
                onScroll={props.onHorizontalScroll}
                pt={props.isPageContainerSpacing ? undefined : '16'}
                pb={props.isPageContainerSpacing ? undefined : '12'}
                px={props.isPageContainerSpacing ? undefined : '4'}
                py={props.isPageContainerSpacing ? undefined : '2'}
                data-testid={props.dataElementName}
                data-element-value={props.dataElementValue}
            >
                {activitiesList.map((activity, index) => (
                    <div key={index}>
                        <ActivityCard
                            index={index}
                            activity={activity}
                            pageTypeId={props.pageTypeId}
                            isPopularActivitiesCard={true}
                            isSearchBoxFeatureGroup={props.isSearchBoxFeatureGroup}
                            elementName={props.dataElementName}
                            shouldShowCityName={false}
                        />
                    </div>
                ))}
            </HorizontalScroll>
        </InView>
    );
};

RecommendedActivitiesCarouselComponent.displayName = 'RecommendedActivitiesCarousel';

export default connect(mapStateToProps)(RecommendedActivitiesCarouselComponent);
export { RecommendedActivitiesCarouselPlaceholder };
